import type { Translation } from "@/i18n/locales/types";

export const platform: Translation["platform"] = {
  title: "Platform",
  backTo: "Back to {{organization}}",
  backToApp: "Back to Punto.Firma",
  tabs: {
    organizations: "Organizations",
    support: "Support access",
    catalog: "Catálogo de webs",
  },
  organizations: {
    description:
      "Onboard organizations and their first admin. Certificates, holders and activity of the organizations are never shown here.",
    new: "New organization",
    search: "Search by name or domain",
    columns: {
      name: "Name",
      domains: "Domains",
      people: "People",
      certificates: "Certificates",
      createdAt: "Created",
      status: "Status",
    },
    empty: "No organizations yet",
    edit: "Edit",
    statuses: {
      active: "Active",
      suspended: "Suspended",
    },
  },
  form: {
    newTitle: "New organization",
    newDescription:
      "People sign in with Google if their domain is listed here and the organization has invited them.",
    editTitle: "Edit “{{name}}”",
    name: "Name",
    slug: "Identifier",
    slugHint:
      "Lowercase letters, numbers and hyphens. Cannot be changed later.",
    domains: "Google Workspace domains",
    domainsHint: "One per line or comma-separated.",
    retention: "Log retention (days)",
    firstAdmin: "First admin",
    firstAdminHint: "Invited as admin; they sign in with Google.",
    create: "Create",
    created: "Organization “{{name}}” created",
    saved: "Organization saved",
    status: "Status",
    suspend: "Suspend organization",
    suspendTitle: "Suspend “{{name}}”?",
    suspendDescription:
      "Nobody in the organization can sign in or sign until it is reactivated. Nothing is deleted.",
    suspendConfirm: "Suspend",
    suspended: "“{{name}}” is suspended",
    reactivate: "Reactivate organization",
    reactivated: "“{{name}}” is active again",
    admins: "Admins",
    adminsEmpty: "No admins yet.",
    addAdmin: "Invite admin",
    adminEmail: "Admin email",
    adminAdded: "{{email}} invited as admin",
    errors: {
      name: "Give it a name.",
      slug: "Only lowercase letters, numbers and hyphens (not at the start or end).",
      domains: "At least one valid domain (e.g. company.com).",
      domain: "“{{domain}}” is not a valid domain.",
      retention: "Between 30 and 3650 days.",
      email: "Enter a valid email.",
    },
  },
  catalog: {
    description:
      "The websites where cloud certificates can be used. Each domain covers its subdomains. Each Mac's proxy CA is only valid for these domains: when a version with new domains is published, every Mac shows “Update allowed websites” and asks for the password once.",
    summary: "Version {{version}} · {{domains}} domains in {{groups}} groups",
    requested:
      "To use “{{domain}}” in a policy, add it to the right group (it is already typed in each one: click “Add” in the right one, or create a new group) and publish the version.",
    groupId: "In rules: {{id}}",
    domainLabel: "Domain",
    domainPlaceholder: "portal.example.es",
    siteLabel: "Site name (optional)",
    sitePlaceholder: "Example City Council",
    add: "Add",
    remove: "Remove {{domain}}",
    added: "New",
    newGroup: "New group",
    newGroupTitle: "New group",
    newGroupDescription:
      "A group bundles domains so policies can use them at once (@identifier).",
    groupIdLabel: "Identifier",
    groupIdHint: "Lowercase, digits, “-” and “_”. Used as @identifier.",
    groupNameLabel: "Name",
    createGroup: "Create group",
    pending:
      "Unpublished changes: {{added}} new domains and {{removed}} removed.",
    pendingSitesOnly: "Unpublished changes to site names.",
    discard: "Discard",
    publish: "Publish version {{version}}",
    publishTitle: "Publish version {{version}} of the catalog?",
    publishDescription:
      "Every organization's policies can use the new domains right away. Each Mac shows “Update allowed websites”; until it is clicked, those websites go through without the cloud certificate.",
    publishNoCa: "No domains change: Macs don't need to update anything.",
    notesLabel: "Reason (kept in the history)",
    published: "Version {{version}} published",
    stale:
      "Someone published another version while you were editing. Reload the catalog.",
    reload: "Reload",
    history: "History",
    historyDescription:
      "Each publication is a new version; earlier ones never change.",
    columns: {
      version: "Version",
      date: "Date",
      by: "By",
      changes: "Changes",
      notes: "Reason",
    },
    migration: "Migration",
    initial: "Initial catalog ({{count}} domains)",
    noChanges: "Site names only",
    errors: {
      domain: "“{{domain}}” is not a valid domain (e.g. portal.example.es).",
      duplicate: "“{{domain}}” is already in the catalog.",
      nested: "“{{domain}}” is already covered by “{{parent}}”.",
      groupId: "Lowercase, digits, “-” and “_” only.",
      groupIdTaken: "There is already a group @{{id}}.",
      groupName: "Give it a name.",
    },
  },
  support: {
    description:
      "Only the access organizations grant from their page. Entering with one is logged on both sides.",
    columns: {
      organization: "Organization",
      who: "For",
      access: "Access",
      reason: "Reason",
      until: "Until",
      status: "Status",
    },
    access: {
      read_only: "View only",
      full: "View and change",
    },
    team: "Support team",
    enter: "Enter",
    entering: "Entering…",
    empty: "No organization has granted support access.",
    status: {
      active: "Active",
      revoked: "Revoked",
      expired: "Expired",
      scheduled: "Scheduled",
    },
  },
};
